'use client';

import { useState, useCallback } from 'react';
import { useMyHrData } from './MeDataLoader';
import type { MeTab } from '@/app/hr/components/types';
import MeHeader from './MeHeader';
import MeOverviewTab from './MeOverviewTab';
import MeReviewsTab from './MeReviewsTab';
import MeCertificationsTab from './MeCertificationsTab';
import MeTimeOffTab from './MeTimeOffTab';
import MeDocumentsTab from './MeDocumentsTab';

const MeInteractive = () => {
  const { loading, error, notLinked, data, reload } = useMyHrData();
  const [activeTab, setActiveTab] = useState<MeTab>('overview');

  const resolveAvatarUrl = useCallback(async (storagePath: string | null): Promise<string | null> => {
    if (!storagePath) return null;
    try {
      const r = await fetch('/api/hr/document-download-url', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ storage_path: storagePath }),
      });
      const json = await r.json();
      return json.signed_url ?? null;
    } catch {
      return null;
    }
  }, []);

  const resolveDocumentDownloadUrl = useCallback(async (storagePath: string): Promise<string | null> => {
    try {
      const r = await fetch('/api/hr/document-download-url', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ storage_path: storagePath }),
      });
      const json = await r.json();
      return json.signed_url ?? null;
    } catch {
      return null;
    }
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ background: '#f8fafc' }}>
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 rounded-full border-4 border-[#E89A3C] border-t-transparent animate-spin" />
          <span className="text-sm text-gray-500">Loading your profile...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen p-8" style={{ background: '#f8fafc' }}>
        <div className="max-w-md mx-auto bg-white rounded-2xl shadow p-6">
          <h2 className="text-lg font-bold text-red-700">Error loading your profile</h2>
          <p className="text-[12px] text-gray-600 mt-2">{error}</p>
          <button onClick={reload} className="mt-4 px-3 py-1.5 rounded-lg text-[12px] font-bold text-white bg-[#E89A3C]">Try again</button>
        </div>
      </div>
    );
  }

  if (notLinked || !data) {
    return (
      <div className="min-h-screen p-8" style={{ background: '#f8fafc' }}>
        <div className="max-w-md mx-auto bg-white rounded-2xl shadow p-6 text-center">
          <h2 className="text-lg font-bold text-[#1C2E5E]">Your profile isn&apos;t linked to an HR record yet</h2>
          <p className="text-[12px] text-gray-600 mt-2">Your login isn&apos;t connected to an employees row. Contact your administrator to provision the link.</p>
        </div>
      </div>
    );
  }

  const tabs: Array<{ key: MeTab; label: string; count?: number }> = [
    { key: 'overview', label: 'Overview' },
    { key: 'reviews', label: 'My Reviews', count: data.reviews.filter(r => r.status === 'released' || r.status === 'acknowledged').length || undefined },
    { key: 'certifications', label: 'My Certifications', count: data.certifications.length || undefined },
    { key: 'time_off', label: 'Time Off', count: data.ptoRequests.filter(r => r.status === 'pending').length || undefined },
    { key: 'documents', label: 'My Documents', count: data.documents.length || undefined },
  ];

  return (
    <div className="min-h-screen" style={{ background: '#f8fafc' }}>
      <div className="sticky top-[60px] z-30 bg-white border-b border-gray-200 shadow-sm">
        <div className="px-6 py-3">
          <h1 className="text-xl font-bold" style={{ color: '#1C2E5E' }}>My Profile</h1>
          <p className="text-xs text-gray-500 mt-0.5">Your HR record, certifications, time off, and documents.</p>
        </div>
      </div>

      <div className="px-6 py-6 max-w-4xl mx-auto">
        <MeHeader employee={data.employee} supervisor={data.supervisor} resolveAvatarUrl={resolveAvatarUrl} />

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="flex items-center gap-1 px-4 border-b border-gray-100 overflow-x-auto">
            {tabs.map(tab => {
              const active = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={`relative px-3 py-3 text-[12px] font-semibold whitespace-nowrap transition-colors ${active ? 'text-[#E89A3C]' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  {tab.label}
                  {tab.count !== undefined && tab.count > 0 && (
                    <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[16px] px-1 rounded-full text-[9px] font-bold bg-gray-200 text-gray-700">{tab.count}</span>
                  )}
                  {active && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#E89A3C] rounded-t" />}
                </button>
              );
            })}
          </div>

          <div className="p-5">
            {activeTab === 'overview' && <MeOverviewTab ctx={data} />}
            {activeTab === 'reviews' && <MeReviewsTab ctx={data} />}
            {activeTab === 'certifications' && <MeCertificationsTab ctx={data} />}
            {activeTab === 'time_off' && <MeTimeOffTab ctx={data} onRequestSubmitted={reload} />}
            {activeTab === 'documents' && <MeDocumentsTab ctx={data} resolveDocumentDownloadUrl={resolveDocumentDownloadUrl} />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeInteractive;
